import React from "react";
import { XCircleIcon } from "@heroicons/react/24/outline";
import moment from "moment";

export interface RejectionBannerProps {
  rejectedBy?: string | null;
  rejectedOn?: string | Date | null;
  rejectionReason?: string | null;
  rejectedAt?: string | null; // Which stage it was rejected at
  onDismiss?: () => void;
  variant?: "banner" | "card";
}

export const RejectionBanner: React.FC<RejectionBannerProps> = ({
  rejectedBy,
  rejectedOn,
  rejectionReason,
  rejectedAt,
  onDismiss,
  variant = "banner",
}) => {
  if (variant === "card") {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 shadow-sm">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <XCircleIcon className="h-6 w-6 text-red-500" />
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-semibold text-red-800">
              Application Rejected
            </h3>
            {rejectedAt && (
              <p className="text-xs text-red-700 mt-1">
                Rejected at: <span className="font-medium">{rejectedAt.replaceAll("_", " ")}</span>
              </p>
            )}
            {rejectedBy && (
              <p className="text-xs text-red-700 mt-1">
                Rejected by: <span className="font-medium">{rejectedBy}</span>
              </p>
            )}
            {rejectedOn && (
              <p className="text-xs text-red-700 mt-1">
                Date: <span className="font-medium">{moment(rejectedOn).format("MMM D, YYYY h:mm A")}</span>
              </p>
            )}
            {rejectionReason && (
              <div className="mt-2 bg-white rounded p-3 border border-red-200">
                <p className="text-xs font-semibold text-red-800 mb-1">Reason for rejection:</p>
                <p className="text-sm text-red-900">{rejectionReason}</p>
              </div>
            )}
            <p className="text-xs text-red-700 mt-3 font-medium">
              Please review the feedback, make necessary changes, and resubmit.
            </p>
          </div>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="ml-3 flex-shrink-0 text-red-500 hover:text-red-700 transition-colors"
            >
              <span className="sr-only">Dismiss</span>
              <XCircleIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Banner variant
  return (
    <div className="bg-red-50 border-l-4 border-red-500 p-3 sm:p-4">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <XCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-red-500" />
        </div>
        <div className="ml-3 flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-red-800">
                Application Rejected
                {rejectedAt && ` at ${rejectedAt.replaceAll("_", " ")}`}
              </p>
              {rejectedBy && (
                <p className="text-xs text-red-700 mt-0.5">
                  by {rejectedBy}
                  {rejectedOn && ` on ${moment(rejectedOn).format("MMM D, YYYY")}`}
                </p>
              )}
            </div>
            {rejectionReason && (
              <p className="text-xs text-red-700 italic max-w-md truncate">
                "{rejectionReason}"
              </p>
            )}
          </div>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="ml-3 flex-shrink-0 text-red-500 hover:text-red-700 transition-colors"
          >
            <span className="sr-only">Dismiss</span>
            <XCircleIcon className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};